<template>
  <v-row>
    <v-col
      cols="12"
      sm="4"
      md="4"
    >
      <VAutocomplete
        ref="RefNetworkType"
        v-model="data.networkType"
        :label="$t('forms.labels.networkType')"
        :items="displayedNetworkTypes"
        itemText="name"
        itemValue="id"
        :loading="loading"
        :disabled="!!data.id"
        :validateAtInitialization="!!data.id"
        outlined
        @change="updateData"
      >
        <template #withInfiniteScroll>
          <div v-intersect="(_, __, isIntersecting) => endIntersect(isIntersecting, 'networkTypes')" />
        </template>
      </VAutocomplete>
    </v-col>
    <v-col
      cols="12"
      sm="4"
      md="4"
    >
      <VAutocomplete
        ref="RefComponentType"
        v-model="data.componentType"
        :label="$t('forms.labels.componentType')"
        :items="displayedComponentTypes"
        itemText="name"
        itemValue="id"
        :loading="loading"
        :disabled="!!data.id"
        :validateAtInitialization="!!data.id"
        outlined
        @change="updateData"
      >
        <template #withInfiniteScroll>
          <div v-intersect="(_, __, isIntersecting)=> endIntersect(isIntersecting, 'componentTypes')" />
        </template>
      </VAutocomplete>
    </v-col>
    <v-col
      cols="12"
      sm="4"
      md="4"
    >
      <VAutocomplete
        ref="analysisTypeAutocompleteRef"
        v-model="data.analysisType"
        :label="$t('forms.labels.analysisType')"
        :items="displayedAnalysisTypes"
        :loading="loading"
        :clearable="true"
        :rules="{ required: true }"
        :disabled="!!data.id"
        :validateAtInitialization="!!data.id"
        outlined
        @search="searchAr"
        @change="clear"
      >
        <template #withInfiniteScroll>
          <div v-intersect="AnalysisEndIntersect" />
        </template>
      </VAutocomplete>
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.veryLow"
        :formatNumber="true"
        :label="$t('sites.norms.veryLow')"
        color="#F44336"
        outlined
        :rules="{decimal: 8, sNorms: smallerThanNorms('veryLow'), bNorms: biggerThanNorms('veryLow'), analysisCondition0To14: conditionError}"
        @input="onInput"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.low"
        :formatNumber="true"
        :label="$t('sites.norms.low')"
        color="#FDD835"
        outlined
        :rules="{decimal: 8, sNorms: smallerThanNorms('low'), bNorms: biggerThanNorms('low'), analysisCondition0To14: conditionError}"
        @input="onInput"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.minimum"
        :formatNumber="true"
        :label="$t('sites.norms.minimum')"
        color="#4CAF50"
        outlined
        :rules="{decimal: 8, sNorms: smallerThanNorms('minimum'), bNorms: biggerThanNorms('minimum'), analysisCondition0To14: conditionError}"
        @input="onInput"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.maximum"
        :formatNumber="true"
        :label="$t('sites.norms.maximum')"
        color="#4CAF50"
        outlined
        :rules="{decimal: 8, sNorms: smallerThanNorms('maximum'), bNorms: biggerThanNorms('maximum'), analysisCondition0To14: conditionError}"
        @input="onInput"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.high"
        :formatNumber="true"
        :label="$t('sites.norms.high')"
        color="#FDD835"
        outlined
        :rules="{decimal: 8, sNorms: smallerThanNorms('high'), bNorms: biggerThanNorms('high'), analysisCondition0To14: conditionError}"
        @input="onInput"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.veryHigh"
        :formatNumber="true"
        :label="$t('sites.norms.veryHigh')"
        color="#F44336"
        outlined
        :rules="{decimal: 8, sNorms: smallerThanNorms('veryHigh'), bNorms: biggerThanNorms('veryHigh'), analysisCondition0To14: conditionError}"
        @input="onInput"
      />
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import analysisTypesConstant from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import { isEmpty } from 'lodash';

export default {
    components: {
        VTextField,
        VAutocomplete,
    },
    props: {
        standard: {
            type: Object,
            default: () => ({}),
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        standards: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            // Backend takes null for 'any'
            networkTypes: [
                {
                    id: null,
                    name: this.$t('forms.labels.any'),
                },
            ],
            componentTypes: [
                {
                    id: null,
                    name: this.$t('forms.labels.any'),
                },
            ],
            analysisTypes: [],
            conditionError: false,
            data: {
                networkType: null,
                componentType: null,
                analysisType: null,
                norm: {
                    veryLow: null,
                    low: null,
                    minimum: null,
                    maximum: null,
                    high: null,
                    veryHigh: null,
                },
            },
            normKeysInvariable: ['veryLow', 'low', 'minimum', 'maximum', 'high', 'veryHigh'],
            loading: false,
            arQueryParams: { perPage: 25 },
            arPagination: {},
            networksTypesPagination: {},
            componentTypesPagination: {},
            networksTypesQueryParams: { page: 1, perpage: 25, search: '' },
            componentTypesQueryParams: { page: 1, perpage: 25, search: '' },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
        ]),
        displayedAnalysisTypes() {
            // Exclude coupon Analysis types
            const analysisTypesExeptCoupons = this.analysisTypes.filter(
                analysisType => analysisType.type !== analysisTypesConstant.TYPE_CORROSION,
            );
            return analysisTypesExeptCoupons.map(({ id, name, measureUnit }) => {
                let displayName = name[this.userLang] || name[this.teamMainLang] || '';
                if (!isEmpty(measureUnit)) {
                    const measure = measureUnit[this.userLang] || measureUnit[this.teamMainLang] || '';
                    displayName += `(${measure})`;
                }
                return {
                    id,
                    name: displayName,
                    disabled: this.isAnalysisTypeUsed(id),
                };
            });
        },
        normKeys() {
            return this.normKeysInvariable;
        },
        displayedNetworkTypes() {
            return this.getDisplayedItem(this.networkTypes);
        },
        displayedComponentTypes() {
            return this.getDisplayedItem(this.componentTypes);
        },
    },
    watch: {
        data: {
            handler() {
                this.updateData();
            },
            deep: true,
        },
    },
    async created() {
        if (this.isEdition) {
            this.data = this.standard;
        }
        await this.getData();
    },
    methods: {
        onInput() {
            this.conditionError = this.analysisTypes[0].condition;
        },
        async getData() {
            this.loading = true;
            await this.getNetworkTypes();
            await this.getComponentTypes();
            await this.getAnalysisTypes('All');
            this.loading = false;
        },
        async getNetworkTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getNetworkTypes({ pagination: false });
                this.networkTypes = data.networkTypes;
            } else if (this.networksTypesPagination?.currentPage !== this.networksTypesQueryParams?.page
            && this.networksTypesPagination?.totalPageCount
                ? this.networksTypesPagination?.totalPageCount >= this.networksTypesQueryParams?.page : true) {
                const { data } = await this.$rq.getNetworkTypes(this.networksTypesQueryParams);
                this.networkTypes = [...data.networkTypes, ...this.networkTypes];
            }
        },
        async getComponentTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getComponentTypes({ pagination: false });
                this.componentTypes = data.componentTypes;
            } else if (this.componentTypesPagination?.currentPage !== this.componentTypesQueryParams?.page
            && this.componentTypesPagination?.totalPageCount
                ? this.componentTypesPagination?.totalPageCount >= this.componentTypesQueryParams?.page : true) {
                const { data } = await this.$rq.getComponentTypes(this.componentTypesQueryParams);
                this.componentTypes = [...data.componentTypes, ...this.componentTypes];
                this.componentTypesPagination = data.pagination;
            }
        },
        async getAnalysisTypes(typeName) {
            if (this.isEdition) {
                // analysisType is not editable so we wont fetch all the ATs
                this.analysisTypes = [this?.standard?.analysisType];
            } else if (isEmpty(this.arPagination) || (this.arPagination?.currentPage !== this.arQueryParams?.page)) {
                const { data } = await this.$rq.getAnalysisTypes({ ...this.arQueryParams, typeName });
                this.analysisTypes = [...this.analysisTypes, ...data.types];
                this.arPagination = data.pagination;
            }
        },
        hasType(standardType, dataType) {
            return standardType ? dataType === standardType.id : dataType === null;
        },
        isAnalysisTypeUsed(id) {
            const existingStandard = this.standards.filter(standard => {
                const { componentType, networkType } = standard;
                return this.hasType(componentType, this.data.componentType)
                && this.hasType(networkType, this.data.networkType);
            });
            const selectedAnalysisTypes = [];
            existingStandard.forEach(standard => { if (standard.analysisType) selectedAnalysisTypes.push(standard.analysisType.id); });
            return selectedAnalysisTypes.includes(id);
        },
        updateData() {
            this.$emit('sendData', this.data);
        },
        smallerThanNorms(normKey) {
            const normIndex = this.normKeys.findIndex(key => key === normKey);
            const normKeysBefore = this.normKeys.slice(0, normIndex);

            return normKeysBefore.map(key => parseFloat(this.data.norm[key]));
        },
        biggerThanNorms(normKey) {
            const normIndex = this.normKeys.findIndex(key => key === normKey);
            const normKeysAfter = this.normKeys.slice(normIndex + 1);

            return normKeysAfter.map(key => parseFloat(this.data.norm[key]));
        },
        async AnalysisEndIntersect(_, __, isIntersecting) {
            const condition = this.arPagination.currentPage < this.arPagination.totalPageCount;
            if (isIntersecting && condition) {
                this.arQueryParams.page = this.arPagination.currentPage + 1;
                await this.getAnalysisTypes();
                // access "v-autocomplete" ref
                const ref = this.$refs?.analysisTypeAutocompleteRef?.$refs?.autocomplete;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async searchAr(search) {
            const { data } = await this.$rq.getAnalysisTypes({ search, pagination: false });
            this.analysisTypes = data.types;
        },
        async clear(val) {
            // when change is clear
            if (!val) {
                this.arPagination = {};
                this.arQueryParams = {};
                this.analysisTypes = [];
                await this.getAnalysisTypes();
            }
        },
        getDisplayedItem(item) {
            return item?.map(({ id, name }) => ({
                id,
                name: id ? name[this.selectedLang] || name[this.teamMainLang] : name,
            }));
        },
        async endIntersectComponentTypes(isIntersecting) {
            const condition = this.componentTypesPagination?.currentPage < this.componentTypesPagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.componentTypesQueryParams.page = this.componentTypesPagination.currentPage + 1;
                await this.getComponentTypes();
                const ref = this.$refs?.refComponentTypes?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersectNetworkTypes(isIntersecting) {
            const condition = this.networksTypesPagination?.currentPage < this.networksTypesPagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.networksTypesQueryParams.page = this.networksTypesPagination.currentPage + 1;
                await this.getNetworkTypes();
                const ref = this.$refs?.refNetworkTypes?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersect(isIntersecting, field) {
            switch (field) {
                case 'componentTypes':
                    await this.endIntersectComponentTypes(isIntersecting);
                    break;
                case 'networkTypes':
                    await this.endIntersectNetworkTypes(isIntersecting);
                    break;
                default:
            }
        },
    },
};
</script>
